import {
  AlertTriangle,
  Loader2,
  Plug,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import {
  customIntegrationsService,
  getUserFacingApiErrorMessage,
} from '../services/api'
import type {
  CustomIntegrationSummary,
} from '../services/api'
import {
  OnboardingEvents,
  trackEvent,
} from '../analytics/posthog'
import {
  copyTextToClipboard,
} from '../utils/clipboard'

const SAMPLE_BUILD_PROMPT = "Build me a custom integration for Linear so I can search and create issues from chat."

function authKindLabel(authKind: string): string {
  switch (authKind) {
    case 'oauth_ac': return 'OAuth'
    case 'api_key': return 'API key'
    case 'basic_auth': return 'Basic auth'
    default: return authKind
  }
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case 'failing': return 'bg-red-100 text-red-700'
    case 'draft': return 'bg-amber-100 text-amber-700'
    case 'ready': return 'bg-green-100 text-green-700'
    default: return 'bg-gray-100 text-gray-600'
  }
}

function initialFor(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '?'
}

function tileColorClass(integration: CustomIntegrationSummary): string {
  if (integration.status === 'failing') {
    return 'bg-red-50 border border-red-200 hover:bg-red-100'
  }
  if (integration.connected) {
    return 'bg-green-50 border-2 border-green-200 hover:bg-green-100'
  }
  return 'bg-white border border-gray-200 hover:border-primary-300 hover:shadow-sm'
}

function CustomIntegrationTile({
  integration,
  onClick,
}: {
  integration: CustomIntegrationSummary
  onClick: () => void
}) {
  const isFailing = integration.status === 'failing'
  return (
    <button
      onClick={onClick}
      className={`w-full flex flex-col items-center justify-center gap-2 p-4 rounded-xl transition-colors relative group ${tileColorClass(integration)}`}
    >
      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 text-white text-sm font-semibold">
        {initialFor(integration.display_name)}
      </div>
      <span className="text-xs font-medium text-gray-700 truncate max-w-full">{integration.display_name}</span>
      {integration.connected && !isFailing && (
        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center shadow-sm">
          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <polyline points="20 6 9 17 4 12" strokeWidth="3" />
          </svg>
        </div>
      )}
      {isFailing && (
        <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center shadow-sm">
          <AlertTriangle className="w-3 h-3 text-white" />
        </div>
      )}
    </button>
  )
}

function EmptyState() {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle')
  const handleCopy = async () => {
    try {
      await copyTextToClipboard(SAMPLE_BUILD_PROMPT)
      setCopyState('copied')
      trackEvent(OnboardingEvents.CUSTOM_INTEGRATION_SAMPLE_PROMPT_COPIED)
    } catch {
      setCopyState('failed')
    }
    setTimeout(() => setCopyState('idle'), 2000)
  }
  return (
    <div className="flex flex-col items-center text-center py-4">
      <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center mb-2">
        <Sparkles className="w-5 h-5 text-primary-500" />
      </div>
      <p className="text-sm text-gray-700 font-medium">Build your own integration</p>
      <p className="text-xs text-gray-500 mt-1 mb-3 max-w-xs">
        Ask your assistant in chat to build a custom integration with any API. It'll show up here once it's ready.
      </p>
      <button
        onClick={() => void handleCopy()}
        className="text-xs font-medium text-primary-600 hover:text-primary-700"
      >
        {copyState === 'copied' ? 'Copied!' : copyState === 'failed' ? 'Copy failed — copy manually' : 'Copy sample prompt'}
      </button>
    </div>
  )
}

function CustomIntegrationDetailModal({
  integration,
  onClose,
  onConnected,
  onDeleted,
}: {
  integration: CustomIntegrationSummary
  onClose: () => void
  onConnected: () => void
  onDeleted: () => void
}) {
  const [busy, setBusy] = useState<'connect' | 'delete' | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleConnect = async () => {
    trackEvent(OnboardingEvents.CUSTOM_INTEGRATION_CONNECT_CLICKED, {
      auth_kind: integration.auth_kind,
      connected: integration.connected,
    })
    setBusy('connect')
    setError(null)
    try {
      const url = integration.auth_kind === 'oauth_ac'
        ? customIntegrationsService.oauthUrl(integration.id)
        : await customIntegrationsService.createSecretRequest(integration.id)
      // busy stays set on success — the modal unmounts when window.location.href navigates away.
      window.location.href = url
      onConnected()
    } catch (err) {
      setError(getUserFacingApiErrorMessage(err, "Couldn't start connecting just now. Give it another try."))
      setBusy(null)
    }
  }

  const handleDelete = async () => {
    setBusy('delete')
    setError(null)
    try {
      await customIntegrationsService.delete(integration.id)
      trackEvent(OnboardingEvents.CUSTOM_INTEGRATION_DELETED)
      onDeleted()
    } catch (err) {
      setError(getUserFacingApiErrorMessage(err, "Couldn't delete this integration just now. Give it another try."))
      setBusy(null)
    }
  }

  const handleClose = () => {
    trackEvent(OnboardingEvents.CUSTOM_INTEGRATION_MODAL_CLOSED)
    onClose()
  }

  const isFailing = integration.status === 'failing'
  const isOAuth = integration.auth_kind === 'oauth_ac'

  let connectLabel: string
  if (isOAuth) {
    connectLabel = integration.connected ? 'Reconnect via OAuth' : 'Connect via OAuth'
  } else {
    connectLabel = integration.connected ? 'Re-enter credentials' : 'Enter credentials'
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-lg w-full">
        <div className="p-6 border-b border-gray-200 flex items-start justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-gradient-to-br from-primary-400 to-primary-600 text-white text-base font-semibold">
              {initialFor(integration.display_name)}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900 truncate">{integration.display_name}</h2>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className="text-xs text-gray-500">{authKindLabel(integration.auth_kind)}</span>
                <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${statusBadgeClass(integration.status)}`}>
                  {integration.status}
                </span>
                {integration.connected && !isFailing && (
                  <span className="px-1.5 py-0.5 text-[10px] font-medium bg-green-100 text-green-700 rounded">
                    Connected
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={handleClose} className="p-1 text-gray-400 hover:text-gray-600 rounded-lg flex-shrink-0">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {isFailing && integration.last_build_error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-xs font-medium text-red-700 mb-1">Last build error</p>
              <p className="text-xs text-red-600 font-mono break-all whitespace-pre-wrap">
                {integration.last_build_error}
              </p>
              <p className="text-xs text-red-700 mt-2">
                Ask your assistant to fix this integration in chat.
              </p>
            </div>
          )}
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">About</p>
            <p className="text-sm text-gray-700">{integration.description}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Internal name</p>
            <code className="text-xs bg-gray-50 px-2 py-1 rounded font-mono text-gray-700">
              {integration.name}
            </code>
          </div>
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex items-center justify-between">
          <button
            onClick={() => { trackEvent(OnboardingEvents.CUSTOM_INTEGRATION_DELETE_CLICKED); setConfirmDelete(true) }}
            disabled={busy !== null}
            className="inline-flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 font-medium disabled:opacity-50"
          >
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          <button
            onClick={() => void handleConnect()}
            disabled={busy !== null}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {busy === 'connect' ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {connectLabel}
          </button>
        </div>

        {confirmDelete && (
          <div className="p-4 border-t border-red-100 bg-red-50">
            <p className="text-sm text-red-800 font-medium mb-2">Delete {integration.display_name}?</p>
            <p className="text-xs text-red-700 mb-3">
              The integration will stop working immediately. Your assistant won't be able to query it. Stored credentials remain encrypted at rest, so an admin can restore the spec if needed.
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => void handleDelete()}
                disabled={busy === 'delete'}
                className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700 disabled:opacity-50"
              >
                {busy === 'delete' ? 'Deleting…' : 'Delete'}
              </button>
              <button
                onClick={() => { trackEvent(OnboardingEvents.CUSTOM_INTEGRATION_DELETE_CANCELLED); setConfirmDelete(false) }}
                disabled={busy === 'delete'}
                className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 rounded-lg text-sm disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function CustomIntegrationsCard() {
  const [integrations, setIntegrations] = useState<CustomIntegrationSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<CustomIntegrationSummary | null>(null)

  const load = useCallback(async () => {
    try {
      const data = await customIntegrationsService.list()
      setIntegrations(data)
    } catch (error) {
      console.error('Failed to load custom integrations:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const handleDeleted = async () => {
    setSelected(null)
    await load()
  }

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-200 p-6 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center">
            <Plug className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Custom Integrations</h3>
            <p className="text-xs text-gray-500">Built by your assistant on demand</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
          </div>
        ) : integrations.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {integrations.map((integration) => (
              <CustomIntegrationTile
                key={integration.id}
                integration={integration}
                onClick={() => {
                  trackEvent(OnboardingEvents.CUSTOM_INTEGRATION_OPENED, { status: integration.status })
                  setSelected(integration)
                }}
              />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <CustomIntegrationDetailModal
          integration={selected}
          onClose={() => setSelected(null)}
          onConnected={() => setSelected(null)}
          onDeleted={() => void handleDeleted()}
        />
      )}
    </>
  )
}
